import {
  DATA_TYPE_MULTI_LINE_STRING,
  DATA_TYPE_DATE,
  DATA_TYPE_MONEY,
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_NUMBER,
  DATA_TYPE_PERCENT,
  DATA_TYPE_NUMBER_HUNDREDTHS,
  DATA_TYPE_STRING,
} from "typeConstants/dataTypes";
import {
  BUILDERS_RISK,
  COMMERCIAL_INT,
  FARM,
  WIND,
  LENDER,
  HIGH_VALUE,
  COMMERCIAL_EXT,
  ELITE_HIGH_VALUE,
  LENDER_COMMERCIAL,
  designationLabelByCode,
  canEditDesignationCode,
} from "typeConstants/designationCodes";
import { sortAlphabeticalAsc } from "components/Shared/Functions";
import { phoneRegExp } from "../../components/Shared/Formatters";
import * as yup from "yup";

export const getFRAddressInformationCard = (fr, stateList, assigneeLabel) => ({
  title: "Address Information",
  fields: [
    { label: `${assigneeLabel || "Field Rep"} #`, value: fr.inspectorLocation.inspectorNumber, editable: false },
    {
      label: "Name", value: fr.inspectorLocation.displayName, editFields: [
        { key: "firstName", label: "First Name", value: fr.inspectorLocation.firstName },
        { key: "lastName", label: "Last Name", value: fr.inspectorLocation.lastName },
      ]
    },
    { key: "address", label: "Address", value: fr.inspectorAddress.address1 },
    {
      label: "City, State Zip", value: `${fr.inspectorAddress.city}, ${fr.inspectorAddress.state} ${fr.inspectorAddress.zip}`, editFields: [
        { key: "city", label: "City", value: fr.inspectorAddress.city },
        { key: "state", label: "State", value: fr.inspectorAddress.state, dataType: DATA_TYPE_DROPDOWN, dataTypeOptions: { options: stateList } },
        { key: "zip", label: "Zip", value: fr.inspectorAddress.zip },
      ]
    },
    { label: "County", value: fr.inspectorAddress.county, editable: false },
  ]
});

export const getFRContactInformationCard = fr => ({
  title: "Contact Information",
  fields: [
    { key: "email", label: "Email", value: fr.email },
    { key: "homePhone", label: "Home Phone", value: fr.homePhone, dataType: DATA_TYPE_PHONE_NUMBER, validation: yup.string().nullable().matches(phoneRegExp, { excludeEmptyString: true, message: "Home Phone is not valid" }) },
    { key: "cellPhone", label: "Cell Phone", value: fr.cellPhone, dataType: DATA_TYPE_PHONE_NUMBER, validation: yup.string().nullable().matches(phoneRegExp, { excludeEmptyString: true, message: "Cell Phone is not valid" }) },
    { key: "county", label: "County", value: fr.inspectorLocation.county, dataType: DATA_TYPE_STRING, editable: false },
    { key: "msa", label: "MSA", value: fr.serviceRegionDisplayName, dataType: DATA_TYPE_STRING, editable: false, scrollable: true },
  ]
});

export const getFRRemarksCard = fr => ({
  title: "Remarks",
  fields: [
    { key: "notes", label: "Notes", value: fr.notes, dataType: DATA_TYPE_MULTI_LINE_STRING, scrollable: true },
  ]
});

export const getFRHRInformationCard = (fr, canEditHrInfo) => ({
  title: "HR Information",
  fields: [
    { key: "payrollEmployeeId", label: "Payroll ID", value: fr.payrollEmployeeId, editable: canEditHrInfo, validation: yup.string().max(10, "Payroll ID must be 10 or less characters.") },
    { label: "Hire Date", value: fr.hireDate, dataType: DATA_TYPE_DATE, editable: false },
    { label: "Full/Part Time", value: fr.employmentType, editable: false },
    { label: "Terminated", value: fr.terminatedDate, dataType: DATA_TYPE_DATE, editable: false },
    { label: "Status", value: fr.employeeStatus, dataType: DATA_TYPE_STRING, editable: false },
    { label: "Background Check", value: fr.backgroundCheckDate, dataType: DATA_TYPE_DATE, editable: false },
    { label: "Hourly Pay Rate", value: fr.hourlyPayRate, dataType: DATA_TYPE_MONEY, editable: false },
    { label: "Team Leader Pay Rate", value: fr.teamLeaderPayRate, dataType: DATA_TYPE_MONEY, editable: false },
  ]
});

export const getFROtherInformationCard = (fr, allTeamLeaders, fieldLevelCoordinators, fieldRepDevelopmentManagers, canEditCanBeAssignedCases, canDisableAutoAssign, canEditProfile) => {
  const fieldDevelopmentEmployee = fr.fieldDevelopmentEmployeeId && fieldRepDevelopmentManagers.find(c => c.employeeId === fr.fieldDevelopmentEmployeeId);
  return {
    title: "Other Information",
    fields: [
      { label: "Online Forms", value: fr.shouldShowOnlineForms, dataType: DATA_TYPE_BOOLEAN, editable: false },
      { label: "Mobile User", value: fr.isMobileUser, dataType: DATA_TYPE_BOOLEAN, editable: false },
      { key: "isFake", label: "Is Fake", value: fr.isFake, dataType: DATA_TYPE_BOOLEAN, editable: true },
      { label: "Abbreviated Web Site", value: fr.usesAbbreviatedWebsite, dataType: DATA_TYPE_BOOLEAN, editable: false },
      {
        label: "Manager", value: fr.managerName,
        editFields: [{
          key: "managerInternetUserId",
          label: "Manager",
          value: fr.managerName && fr.managerInternetUserId && { label: fr.managerName, value: fr.managerInternetUserId },
          dataType: DATA_TYPE_DROPDOWN,
          dataTypeOptions: {
            options: fieldLevelCoordinators.map(x => ({ label: `${x.firstName} ${x.lastName}`, value: x.internetUserId }))
              .sort(sortAlphabeticalAsc),
            nullable: false
          }
        }]
      },
      { label: "Client Code", value: fr.clientCode, editable: false },
      {
        label: "Team Leader", value: fr.teamLeader && fr.teamLeader.firstLastName,
        editFields: [{
          key: "teamLeader",
          label: "Team Leader",
          value: fr.teamLeader && { label: fr.teamLeader.firstLastName, value: fr.teamLeader.inspectorNumber },
          dataType: DATA_TYPE_DROPDOWN,
          dataTypeOptions: {
            options: allTeamLeaders.map(x => ({ label: x.firstLastName, value: x.inspectorNumber })),
            nullable: false
          }
        }]
      },
      { key: "canBeAssignedCases", label: "Can Be Manually Assigned Cases", value: fr.canBeAssignedCases, dataType: DATA_TYPE_BOOLEAN, editable: canEditCanBeAssignedCases },
      { key: "disableAutoAssign", label: "Can Be Auto-Assigned Cases", value: !fr.disableAutoAssign, dataType: DATA_TYPE_BOOLEAN, editable: canDisableAutoAssign },
      { key: "selfAssignEnabled", label: "Can Self Assign Cases", value: fr.selfAssignEnabled, dataType: DATA_TYPE_BOOLEAN, editable: canEditProfile },
      { key: "trainingCompletedDate", label: "Training Completed", value: fr.trainingCompletedDate, dataType: DATA_TYPE_DATE },
      {
        label: "Field Development Manager", value: fieldDevelopmentEmployee && fieldDevelopmentEmployee.firstLastName,
        editFields: [{
          key: "fieldDevelopmentEmployeeId",
          label: "Field Development Manager",
          value: fieldDevelopmentEmployee && { label: fieldDevelopmentEmployee.firstLastName, value: fieldDevelopmentEmployee.employeeId },
          dataType: DATA_TYPE_DROPDOWN,
          dataTypeOptions: {
            options: fieldRepDevelopmentManagers.map(x => ({ label: x.firstLastName, value: x.employeeId })),
            nullable: true
          }
        }]
      },
      { key: "lastAuditDate", label: "Last Audit Date", value: fr.lastAuditDate, dataType: DATA_TYPE_DATE },
    ]
  };
};

export const getFRDetailsCard = fr => ({
  title: "Details",
  fields: [
    { label: "Number of Cases", value: fr.countCases },
    { label: "Assigned Today", value: fr.countCasesAssignedToday, dataType: DATA_TYPE_NUMBER },
    { label: "Past Due", value: fr.countPastDueCases, dataType: DATA_TYPE_NUMBER },
    { label: "Rejected", value: fr.countRejectedCases, dataType: DATA_TYPE_NUMBER },
    { label: "Error Percent", value: fr.errorPercent, dataType: DATA_TYPE_PERCENT },
    { label: "Interior Completion Percent", value: fr.interiorCompletionPercent, dataType: DATA_TYPE_PERCENT },
    { label: "Exterior Completion Percent", value: fr.exteriorCompletionPercent, dataType: DATA_TYPE_PERCENT },
    { label: "Past Due Percent", value: fr.pastDuePercent, dataType: DATA_TYPE_PERCENT },
    { label: "Max Weekly Estimated Hours (FHE)", value: fr.workCapacityWeeklyHours, dataType: DATA_TYPE_NUMBER_HUNDREDTHS },
  ]
});

export const getFRDesignationsCard = ({ fieldRep: { designationCodes }, onEditFinished, userRights }) => ({
  title: "Designations",
  onEditFinished: keyValues => {
    const newDesignations = keyValues
      .filter(({ value }) => value)
      .map(({ key }) => key);
    const existingDesignations = designationCodes
      .filter(dc => !newDesignations.includes(dc))
      .filter(dc => !keyValues.filter(({ value }) => !value).map(({ key }) => key).includes(dc));
    return (
      onEditFinished([{
        key: "designations",
        value: newDesignations.concat(existingDesignations),
      }])
    );
  },
  fields: [BUILDERS_RISK, COMMERCIAL_EXT, COMMERCIAL_INT, FARM, HIGH_VALUE, LENDER, WIND, ELITE_HIGH_VALUE, LENDER_COMMERCIAL].map(designationCode => ({
    key: designationCode,
    label: designationLabelByCode[designationCode],
    value: designationCodes.includes(designationCode),
    dataType: DATA_TYPE_BOOLEAN,
    editable: canEditDesignationCode(designationCode, userRights),
  }))
});
